import { onMounted, onBeforeUnmount } from 'vue';
import { useMessagingStore, type Conversation } from '@/stores/messagingStore';
import { logDebug, logInfo, logWarn } from '@/utils/secureLogger';

/**
 * Message types for cross-tab communication
 */
export type SyncMessageType =
  | 'CONVERSATION_READ'
  | 'NEW_MESSAGE'
  | 'CONVERSATION_UPDATE'
  | 'UNREAD_COUNTS_UPDATE'
  | 'ACTIVE_CONVERSATION_CHANGE';

/**
 * Sync message structure
 */
export interface SyncMessage {
  type: SyncMessageType;
  conversationId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  timestamp: number;
  sourceTabId: string;
}

/**
 * Composable for cross-tab synchronization using BroadcastChannel API
 * Replaces localStorage-based coordination with real-time event-driven sync
 */
export const useMessagingSync = (userType: 'student' | 'tutor') => {
  const store = useMessagingStore();
  const channelName = `elevateED-messaging-${userType}`;
  let channel: BroadcastChannel | null = null;
  const tabId = `tab-${Date.now()}-${Math.random().toString(36).substring(7)}`;

  /**
   * Initialize BroadcastChannel
   */
  const initializeChannel = () => {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === 'undefined') {
      logWarn('BroadcastChannel not supported, falling back to localStorage events');
      return;
    }

    try {
      channel = new BroadcastChannel(channelName);

      // Listen for messages from other tabs
      channel.onmessage = (event: MessageEvent<SyncMessage>) => {
        const message = event.data;

        // Ignore messages from this tab
        if (message.sourceTabId === tabId) {
          return;
        }

        handleSyncMessage(message);
      };

      logInfo('BroadcastChannel initialized', { channelName, tabId });
    } catch (error) {
      logWarn('Failed to initialize BroadcastChannel', { error });
    }
  };

  /**
   * Handle incoming sync message from another tab
   */
  const handleSyncMessage = (message: SyncMessage) => {
    logDebug('Received sync message', {
      type: message.type,
      conversationId: message.conversationId?.substring(0, 8) + '***',
      sourceTabId: message.sourceTabId
    });

    switch (message.type) {
      case 'CONVERSATION_READ':
        if (message.conversationId) {
          // Mark as read locally without broadcasting again
          store.explicitlyRead.add(message.conversationId);
          store.unreadCounts.delete(message.conversationId);
          
          const conversation = store.getConversation(message.conversationId);
          if (conversation) {
            conversation.isExplicitlyRead = true;
            conversation.unreadCount = 0;
            store.updateConversation(conversation);
          }
          
          logInfo('Synced conversation read from another tab', {
            conversationId: message.conversationId.substring(0, 8) + '***'
          });
        }
        break;

      case 'NEW_MESSAGE':
        if (message.conversationId && message.data) {
          const msgData = message.data as { message: string; timestamp: number; senderId: string };
          store.handleNewMessage(
            message.conversationId,
            msgData.message,
            msgData.timestamp,
            msgData.senderId
          );
          logInfo('Synced new message from another tab', {
            conversationId: message.conversationId.substring(0, 8) + '***'
          });
        }
        break;

      case 'CONVERSATION_UPDATE':
        if (message.data) {
          const convData = message.data as Conversation;
          store.updateConversation(convData);
          logInfo('Synced conversation update from another tab', {
            conversationId: convData.id.substring(0, 8) + '***'
          });
        }
        break;

      case 'UNREAD_COUNTS_UPDATE':
        if (message.data) {
          store.setUnreadCounts(message.data as Record<string, number>);
          logInfo('Synced unread counts from another tab', {
            conversationsWithUnread: Object.keys(message.data).length
          });
        }
        break;

      case 'ACTIVE_CONVERSATION_CHANGE':
        if (message.conversationId !== undefined) {
          store.setActiveConversation(message.conversationId);
          logInfo('Synced active conversation from another tab', {
            conversationId: message.conversationId?.substring(0, 8) + '***'
          });
        }
        break;

      default:
        logWarn('Unknown sync message type', { type: message.type });
    }
  };

  /**
   * Broadcast a message to other tabs
   */
  const broadcast = (
    type: SyncMessageType,
    conversationId?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any
  ) => {
    if (!channel) {
      logDebug('BroadcastChannel not available, skipping broadcast');
      return;
    }

    const message: SyncMessage = {
      type,
      conversationId,
      data,
      timestamp: Date.now(),
      sourceTabId: tabId
    };

    try {
      channel.postMessage(message);
      logDebug('Broadcasted message', {
        type,
        conversationId: conversationId?.substring(0, 8) + '***'
      });
    } catch (error) {
      logWarn('Failed to broadcast message', { error, type });
    }
  };

  /**
   * Broadcast that a conversation was marked as read
   */
  const broadcastConversationRead = (conversationId: string) => {
    broadcast('CONVERSATION_READ', conversationId);
  };

  /**
   * Broadcast a new message
   */
  const broadcastNewMessage = (
    conversationId: string,
    message: string,
    timestamp: number,
    senderId: string
  ) => {
    broadcast('NEW_MESSAGE', conversationId, {
      message,
      timestamp,
      senderId
    });
  };

  /**
   * Broadcast a conversation update
   */
  const broadcastConversationUpdate = (conversation: Conversation) => {
    broadcast('CONVERSATION_UPDATE', conversation.id, conversation);
  };

  /**
   * Broadcast unread counts update
   */
  const broadcastUnreadCountsUpdate = (countMap: Record<string, number>) => {
    broadcast('UNREAD_COUNTS_UPDATE', undefined, countMap);
  };

  /**
   * Broadcast active conversation change
   */
  const broadcastActiveConversationChange = (conversationId: string | null) => {
    broadcast('ACTIVE_CONVERSATION_CHANGE', conversationId || undefined);
  };

  /**
   * Close the channel
   */
  const closeChannel = () => {
    if (channel) {
      channel.close();
      channel = null;
      logInfo('BroadcastChannel closed', { channelName });
    }
  };

  // Lifecycle hooks
  onMounted(() => {
    initializeChannel();
  });

  onBeforeUnmount(() => {
    closeChannel();
  });

  return {
    broadcast,
    broadcastConversationRead,
    broadcastNewMessage,
    broadcastConversationUpdate,
    broadcastUnreadCountsUpdate,
    broadcastActiveConversationChange,
    closeChannel,
    tabId
  };
};
